import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const next = new URLSearchParams(location.search).get("next") || "/";

  const submit = async (e) => {
    e.preventDefault();
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail || !password) { toast.error("Enter email & password"); return; }
    setLoading(true);
    try {
      await login(cleanEmail, password);
      toast.success("Logged in");
      navigate(next);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid email or password");
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-16" data-testid="login-page">
      <h1 className="font-display text-5xl">LOG IN</h1>
      <p className="font-mono-b text-sm mt-2 text-black/70">Welcome back, outlaw.</p>
      <form onSubmit={submit} className="mt-6 space-y-4" autoComplete="on">
        <div>
          <label className="text-xs font-mono-b font-bold uppercase">EMAIL</label>
          <input
            data-testid="login-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            required
            autoComplete="email"
            spellCheck={false}
            autoCapitalize="none"
            className="brutal-input w-full mt-1"
          />
        </div>
        <div>
          <label className="text-xs font-mono-b font-bold uppercase">PASSWORD</label>
          <div className="relative mt-1">
            <input
              data-testid="login-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              type={showPw ? "text" : "password"}
              required
              autoComplete="current-password"
              className="brutal-input w-full pr-12"
            />
            <button
              type="button"
              data-testid="login-toggle-pw"
              onClick={() => setShowPw(!showPw)}
              aria-label={showPw ? "Hide password" : "Show password"}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-black/70 hover:text-black"
            >
              {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>
        <button data-testid="login-submit" disabled={loading} type="submit" className="w-full bg-black text-[#D4FF00] border-2 border-black brutal-shadow hover-nudge py-3 font-mono-b font-bold uppercase disabled:opacity-50">
          {loading ? "..." : "LOG IN"}
        </button>
      </form>
      <div className="flex items-center gap-3 my-6">
        <div className="flex-1 h-[2px] bg-black" />
        <span className="font-mono-b text-xs">OR</span>
        <div className="flex-1 h-[2px] bg-black" />
      </div>
      <button data-testid="login-google" onClick={googleLogin} className="w-full bg-white border-2 border-black brutal-shadow hover-nudge py-3 font-mono-b font-bold uppercase">
        CONTINUE WITH GOOGLE
      </button>
      <p className="mt-6 text-sm font-mono-b">New here? <Link to="/register" data-testid="login-to-register" className="underline font-bold">Create an account</Link></p>
    </div>
  );
}
